import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  MoreHorizontal,
  TrendingUp,
  Users,
  LogOut,
  ChefHat,
  Activity,
  ClipboardList,
  ShoppingCart,
  UtensilsCrossed,
  BookOpen,
  BarChart3,
  ShieldCheck
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import NotificationsDropdown from './NotificationsDropdown';

const allNavItems = [
  { to: '/', icon: LayoutDashboard, label: 'Swipe', mobileLabel: 'Swipe', exact: true, permission: 'view_own_tasks' },
  { to: '/gerer', icon: ClipboardList, label: 'Mise en Place', mobileLabel: 'M.E.P', permission: 'view_own_tasks' },
  { to: '/commande', icon: ShoppingCart, label: 'Commande', mobileLabel: 'Cmd', permission: ['view_own_order_requests', 'view_orders', 'create_order', 'receive_order'] },
  { to: '/menu', icon: UtensilsCrossed, label: 'Menu', mobileLabel: 'Menu', permission: 'view_menu' },
  { to: '/recettes', icon: BookOpen, label: 'Recettes', mobileLabel: 'Recettes', permission: 'view_recipes' },
  { to: '/production', icon: Activity, label: 'Production', mobileLabel: 'Prod.', permission: 'view_production' },
  { to: '/sales', icon: TrendingUp, label: 'Ventes', mobileLabel: 'Ventes', permission: 'view_sales' },
  { to: '/rapports', icon: BarChart3, label: 'Rapports', mobileLabel: 'Rapports', permission: 'view_reports' },
  { to: '/team', icon: Users, label: 'Équipe', mobileLabel: 'Équipe', permission: 'view_team' },
  { to: '/administration', icon: ShieldCheck, label: 'Administration', mobileLabel: 'Admin', permission: 'manage_roles' },
];

// Visibilité pilotée par la matrice de permissions (user.permissions) ; l'onglet Swipe est commun à tous les rôles
const getNavItems = (role, user) => {
  const allowed = (item) => !item.permission || [].concat(item.permission).some(p => (user?.permissions || []).includes(p));
  return allNavItems.filter(allowed);
};

// Desktop Sidebar
export const Sidebar = () => {
  const { user, logout } = useAuth();

  return (
    <aside className="hidden md:flex flex-col w-64 bg-white border-r border-gray-200 h-screen sticky top-0">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Chef's Connect" className="w-10 h-10 rounded-xl" />
            <div>
              <h1 className="font-heading font-bold text-lg text-gray-900">Chef's Connect</h1>
              <p className="text-xs text-gray-400">Odoo Kitchen</p>
            </div>
          </div>
          <NotificationsDropdown />
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {getNavItems(user?.role, user).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.exact}
            className={({ isActive }) => `
              flex items-center gap-3 px-4 py-3 rounded-xl transition-all
              ${isActive
                ? 'bg-neon-pink/10 text-neon-pink font-semibold border-l-2 border-neon-pink'
                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
              }
            `}
          >
            {({ isActive }) => (
              <>
                <item.icon size={20} strokeWidth={isActive ? 2.5 : 2} />
                <span>{item.label}</span>
                {isActive && (
                  <motion.div
                    layoutId="nav-indicator"
                    className="ml-auto w-1.5 h-1.5 bg-neon-pink rounded-full shadow-neon-pink"
                  />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-amber-50 border border-amber-300 rounded-full flex items-center justify-center">
            <span className="text-amber-700 font-bold text-sm">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 truncate">{user?.name || 'Chef'}</p>
            <p className="text-xs text-gray-400 truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="flex items-center gap-2 w-full px-4 py-2 text-gray-500 hover:text-red-600 hover:bg-red-500/10 rounded-xl transition-all"
        >
          <LogOut size={18} strokeWidth={2} />
          <span>Déconnexion</span>
        </button>
      </div>
    </aside>
  );
};

// Mobile Bottom Navigation : 4 onglets principaux + « Plus » (grille des autres modules) — plus rien hors écran
const MAX_PRIMARY = 4;
export const BottomNav = () => {
  const { user, logout } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const items = getNavItems(user?.role, user);
  const primary = items.length > MAX_PRIMARY + 1 ? items.slice(0, MAX_PRIMARY) : items;
  const more = items.length > MAX_PRIMARY + 1 ? items.slice(MAX_PRIMARY) : [];
  const moreActive = more.some(i => (i.exact ? location.pathname === i.to : location.pathname.startsWith(i.to)));
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  const tabCls = (active) => `flex flex-col items-center justify-center py-1.5 px-2 min-w-0 flex-1 rounded-xl transition-all ${active ? 'text-neon-pink bg-neon-pink/10' : 'text-gray-400'}`;

  return (
    <>
      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setMoreOpen(false)} data-testid="bottom-nav-more-backdrop">
          <div className="absolute bottom-[64px] left-0 right-0 bg-white rounded-t-3xl border-t border-gray-200 p-4 pb-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="bottom-nav-more-sheet">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Tous les modules</p>
            <div className="grid grid-cols-4 gap-2">
              {more.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.exact} onClick={() => setMoreOpen(false)} data-testid={`bottom-nav-more-${item.to.replace('/', '') || 'swipe'}`}
                  className={({ isActive }) => `flex flex-col items-center gap-1 py-3 rounded-2xl min-h-[64px] ${isActive ? 'bg-neon-pink/10 text-neon-pink' : 'bg-gray-50 text-gray-700'}`}>
                  <item.icon size={20} />
                  <span className="text-[11px] font-medium text-center leading-tight">{item.label}</span>
                </NavLink>
              ))}
              <button type="button" onClick={logout} className="flex flex-col items-center gap-1 py-3 rounded-2xl min-h-[64px] bg-red-50 text-red-600" data-testid="bottom-nav-logout-btn">
                <LogOut size={20} /><span className="text-[11px] font-medium">Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      )}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-t border-gray-200 safe-bottom" data-testid="bottom-nav">
        <div className="flex px-1 py-1.5">
          {primary.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.exact} className={({ isActive }) => tabCls(isActive)} data-testid={`bottom-nav-${item.to.replace('/', '') || 'swipe'}`}>
              {({ isActive }) => (
                <>
                  <item.icon size={18} strokeWidth={isActive ? 2.5 : 2} />
                  <span className="text-[9px] mt-0.5 font-medium whitespace-nowrap">{item.mobileLabel}</span>
                </>
              )}
            </NavLink>
          ))}
          {more.length > 0 && (
            <button type="button" onClick={() => setMoreOpen(v => !v)} className={tabCls(moreOpen || moreActive)} aria-expanded={moreOpen} data-testid="bottom-nav-more-btn">
              <MoreHorizontal size={18} strokeWidth={moreOpen || moreActive ? 2.5 : 2} />
              <span className="text-[9px] mt-0.5 font-medium">Plus</span>
            </button>
          )}
        </div>
      </nav>
    </>
  );
};
